import React, { useCallback, useState } from "react";
import {
  Button,
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
  Icon,
  Menu,
  MenuPopup,
  MenuTrigger,
} from "@salestrak/ui";
import { useIssueActions, type IssueActionRef } from "#app/hooks/kanban/use-issue-actions";
import { useDeleteIssueWithUndo } from "#app/hooks/kanban/use-delete-issue-with-undo";
import { useItemShareDialogActions } from "#app/lib/item-share-dialog-context";
import { useAutomationRunsDialogActions } from "#app/lib/automation-runs-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import {
  CONTEXT_MENU_PARTS,
  renderIssueMenuItems,
} from "./issue-menu-items";
import { RenameIssueDialog } from "./rename-issue-dialog";
import type { Omittable } from "@salestrak/ui";

type IssueMenuProps = Omittable<{
  issue: IssueActionRef;
  /** "dropdown" (default) renders a three-dot button. "context" wraps children. */
  type?: "dropdown" | "context";
  /** The element to wrap when type="context". */
  children?: React.ReactNode;
  /** Extra menu items prepended before the standard ones. */
  prepend?: React.ReactNode;
  /** When true, show only the non-mutating items (Favorite + Copy
   *  link). For view / comment access tiers in the issue view. */
  readOnly?: boolean;
}>;

export function IssueMenu({
  issue,
  type = "dropdown",
  children,
  prepend,
  readOnly,
}: IssueMenuProps) {
  const actions = useIssueActions(issue);
  const deleteWithUndo = useDeleteIssueWithUndo();
  const { openDialog: openShareDialog } = useItemShareDialogActions();
  const canShare = canManageSharing(issue.viewerAccessLevel);
  // Per-issue resolved gates — the backend already knows whether the
  // viewer's role + ownership + share state allow the action, so the
  // menu just reads the booleans. Default to false when missing so a
  // stale fragment can't briefly expose a row the user can't actually
  // act on.
  const canDelete = !!issue.viewerCanDelete;
  const canArchive = !!issue.viewerCanArchive;
  const handleShare = useCallback(() => {
    openShareDialog(issue.issueId);
  }, [openShareDialog, issue.issueId]);

  // "Automation activity" — same gating as the rest of automations: the
  // feature flag AND the manage permission (the dialog spans every automation
  // in the workspace, so it's an automations-admin surface). Only wired into
  // the menu when both hold, so the row (and its handler) stay absent
  // otherwise.
  const automationsEnabled = useAutomationsEnabled();
  const { canViewRuns } = useAutomationPermissions();
  const showAutomationRuns = automationsEnabled && canViewRuns;
  const { openDialog: openRunsDialog } = useAutomationRunsDialogActions();
  const handleAutomationRuns = useCallback(() => {
    openRunsDialog({ issueId: issue.issueId, issueName: issue.name });
  }, [openRunsDialog, issue.issueId, issue.name]);

  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const handleRenameOpen = useCallback(() => {
    setRenameValue(issue.name);
    setShowRename(true);
  }, [issue.name]);

  // Destructured so the callback depends on the one action it calls, not on
  // the whole action bag.
  const { renameIssue } = actions;
  const handleRenameConfirm = useCallback(async () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== issue.name) {
      await renameIssue(issue.issueId, trimmed);
    }
    setShowRename(false);
  }, [renameValue, issue.issueId, issue.name, renameIssue]);

  const handleDelete = useCallback(() => {
    void deleteWithUndo(issue);
  }, [deleteWithUndo, issue]);

  const items = (parts?: typeof CONTEXT_MENU_PARTS) => (
    <>
      {prepend}
      {renderIssueMenuItems({
        ...actions,
        parts,
        onAutomationRuns: showAutomationRuns ? handleAutomationRuns : undefined,
        onRename: handleRenameOpen,
        onDelete: handleDelete,
        onShare: handleShare,
        canManageSharing: canShare,
        canDelete,
        canArchive,
        readOnly,
      })}
    </>
  );

  return (
    <>
      {type === "context" ? (
        <ContextMenu>
          <ContextMenuTrigger>{children}</ContextMenuTrigger>
          <ContextMenuPopup className="min-w-[220px]">
            {items(CONTEXT_MENU_PARTS)}
          </ContextMenuPopup>
        </ContextMenu>
      ) : (
        <Menu>
          <MenuTrigger
            render={
              <Button
                aria-label="Project actions"
                variant="ghost"
                size="icon-sm"
              />
            }
          >
            <Icon name="more-horizontal" />
          </MenuTrigger>
          <MenuPopup align="start" className="min-w-[220px]">
            {items()}
          </MenuPopup>
        </Menu>
      )}

      <RenameIssueDialog
        open={showRename}
        onOpenChange={setShowRename}
        value={renameValue}
        onValueChange={setRenameValue}
        onConfirm={handleRenameConfirm}
      />
    </>
  );
}
